'use client';

import { useEffect, useState } from 'react';
import type { DiscountCodeResponse } from '@sharpr/types';
import {
  clearStoredReferralCode,
  getStoredReferralCode,
  storeReferralCode,
} from '@/utils/referralStorage';

export interface AppliedDiscount {
  percentOff?: number;
  amountOffCents?: number;
}

/**
 * Referral/promo-code state for the pricing surface. On mount applies an explicit
 * `?ref=` `urlCode` (errors surface) else silently revalidates the stored code: a
 * stale stored code never errors for a visitor who typed nothing — it clears itself.
 */
export function useReferralCode(urlCode?: string) {
  const [appliedReferralCode, setAppliedReferralCode] = useState<string | null>(null);
  const [appliedDiscountLabel, setAppliedDiscountLabel] = useState<string | null>(null);
  const [appliedDiscount, setAppliedDiscount] = useState<AppliedDiscount | null>(null);
  const [referralInput, setReferralInput] = useState('');
  const [referralError, setReferralError] = useState<string | null>(null);
  const [referralValidating, setReferralValidating] = useState(false);
  const [showReferralInput, setShowReferralInput] = useState(false);

  const validateAndApplyReferralCode = async (
    code: string,
    opts: { silent?: boolean } = {},
  ) => {
    setReferralValidating(true);
    setReferralError(null);
    try {
      const response = await fetch(`/api/billing/referral-code?code=${encodeURIComponent(code)}`);
      // Only an explicit 200 {valid:false} is a verdict; 403/429/5xx are transient and
      // must not drop attribution.
      const json = response.ok ? ((await response.json()) as DiscountCodeResponse) : null;
      if (json && json.valid) {
        storeReferralCode(json.code);
        setAppliedReferralCode(json.code);
        setAppliedDiscountLabel(json.discount?.label ?? null);
        setAppliedDiscount(
          json.discount
            ? { percentOff: json.discount.percentOff, amountOffCents: json.discount.amountOffCents }
            : null,
        );
        setReferralInput('');
        setShowReferralInput(false);
      } else if (json && json.valid === false) {
        // Stale stored code self-clears; manual / ?ref= surfaces the error.
        if (opts.silent) clearStoredReferralCode();
        else setReferralError('Invalid code');
      } else {
        // Transient: keep any stored code, surface only if user-initiated.
        if (!opts.silent) setReferralError('Failed to validate code');
      }
    } catch {
      // Transient network failure: keep the stored code, surface error only if user-initiated.
      if (!opts.silent) setReferralError('Failed to validate code');
    } finally {
      setReferralValidating(false);
    }
  };

  useEffect(() => {
    if (urlCode) {
      validateAndApplyReferralCode(urlCode); // explicit link — errors surface
    } else {
      // Silent revalidation of a stored code: stale codes clear themselves, no error.
      const stored = getStoredReferralCode();
      if (stored) validateAndApplyReferralCode(stored, { silent: true });
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  return {
    appliedReferralCode,
    appliedDiscountLabel,
    appliedDiscount,
    referralInput,
    setReferralInput,
    referralError,
    setReferralError,
    referralValidating,
    showReferralInput,
    setShowReferralInput,
    validateAndApplyReferralCode,
  };
}
